import { useState, useMemo, useRef, useCallback } from "react";
import { X, Maximize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { Reveal } from "@/components/common/Reveal";
import { useModalEffects } from "@/hooks/useModalEffects";
import { GALLERY, GALLERY_FILTERS } from "@/utils/constants";

export const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const lightboxRef = useRef(null);
  const closeLightbox = useCallback(() => setActive(null), []);
  useModalEffects(!!active, closeLightbox, lightboxRef);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );

  return (
    <Section id="gallery" testId="gallery-section" bg="charcoal" blueprint blueprintOpacity={0.18}>
      <Reveal>
        <p className="text-weld-glow font-semibold tracking-[0.25em] uppercase text-sm mb-3">Portfolio</p>
        <h2 className="font-display text-5xl md:text-6xl text-white">Gallery</h2>
      </Reveal>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
        {GALLERY_FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wide transition-all ${
              filter === f
                ? "bg-weld text-ink"
                : "border border-white/15 text-white/70 hover:border-weld/50 hover:text-weld-glow"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <motion.div layout className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <AnimatePresence mode="popLayout">
          {items.map((img, i) => (
            <motion.button
              key={img.title}
              layout
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(img)}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.35 }}
              className="group relative overflow-hidden rounded-xl aspect-square border border-white/10 hover:border-weld/50"
            >
              <img src={img.image} alt={img.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                <Maximize2 className="text-weld-glow mb-2" size={18} />
                <span className="text-xs text-white/90 font-medium">{img.title}</span>
                <span className="text-[10px] text-weld-glow uppercase tracking-wider">{img.category}</span>
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] bg-ink/95 backdrop-blur-md flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            data-lenis-prevent
          >
            <button data-testid="lightbox-close" aria-label="Close image" className="fixed top-6 right-6 z-[80] text-white/80 hover:text-weld-glow" onClick={closeLightbox}>
              <X size={32} />
            </button>
            <motion.div ref={lightboxRef} role="dialog" aria-modal="true" aria-label={active.title} tabIndex={-1} className="relative max-w-4xl w-full outline-none" initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={(e) => e.stopPropagation()}>
              <img src={active.image} alt={active.title} className="w-full max-h-[80vh] object-contain rounded-xl border border-weld/30" />
              <div className="mt-4 text-center">
                <p className="text-weld-glow text-xs font-semibold uppercase tracking-[0.2em]">{active.category}</p>
                <h3 className="font-display text-3xl text-white mt-1">{active.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
};
